type RetailerUrlInput = {
  url?: string | null;
  retailer?: string | null;
  storeId?: string | null;
};

const RETAILER_HOSTS: Record<string, string> = {
  amazon: "https://www.amazon.com",
  bestbuy: "https://www.bestbuy.com",
  costco: "https://www.costco.com",
  dollar_general: "https://www.dollargeneral.com",
  cvs: "https://www.cvs.com",
  home_depot: "https://www.homedepot.com",
  kohls: "https://www.kohls.com",
  lowes: "https://www.lowes.com",
  tractor_supply: "https://www.tractorsupply.com",
  northern_tool: "https://www.northerntool.com",
  samsclub: "https://www.samsclub.com",
  ace_hardware: "https://www.acehardware.com",
  target: "https://www.target.com",
  target_online: "https://www.target.com",
  walmart: "https://www.walmart.com",
  walmart_online: "https://www.walmart.com",
  woot: "https://www.woot.com",
  newegg: "https://www.newegg.com",
  gamestop: "https://www.gamestop.com",
  bjs: "https://www.bjs.com",
  meijer: "https://www.meijer.com",
  menards: "https://www.menards.com",
  academy: "https://www.academy.com",
  walgreens: "https://www.walgreens.com",
  zappos: "https://www.zappos.com",
  jcpenney: "https://www.jcpenney.com",
  belk: "https://www.belk.com",
  michaels: "https://www.michaels.com",
  nike: "https://www.nike.com",
  adidas: "https://www.adidas.com",
  reebok: "https://www.reebok.com",
  skechers: "https://www.skechers.com",
  puma: "https://us.puma.com",
  underarmour: "https://www.underarmour.com",
  columbia: "https://www.columbia.com",
  nordstromrack: "https://www.nordstromrack.com",
  aldo: "https://www.aldoshoes.com",
  vitacost: "https://www.vitacost.com",
  journeys: "https://www.journeys.com",
  shopdisney: "https://www.disneystore.com",
  ninjakitchen: "https://www.ninjakitchen.com",
  hottopic: "https://www.hottopic.com",
  officedepot: "https://www.officedepot.com",
  barnesnoble: "https://www.barnesandnoble.com",
  razer: "https://www.razer.com",
  pacsun: "https://www.pacsun.com",
  bathbodyworks: "https://www.bathandbodyworks.com",
  staples: "https://www.staples.com",
  petsmart: "https://www.petsmart.com",
  ulta: "https://www.ulta.com",
  forever21: "https://www.forever21.com",
  gap: "https://www.gap.com",
  petco: "https://www.petco.com",
  americaneagle: "https://www.ae.com",
  aerie: "https://www.ae.com",
  carters: "https://www.carters.com",
  abercrombie: "https://www.abercrombie.com",
  hollister: "https://www.hollisterco.com",
  oldnavy: "https://oldnavy.gap.com",
  bananarepublic: "https://bananarepublic.gap.com",
  stevemadden: "https://www.stevemadden.com",
  athleta: "https://athleta.gap.com",
  gapfactory: "https://www.gapfactory.com",
  bananarepublicfactory: "https://bananarepublicfactory.gapfactory.com",
  boxlunch: "https://www.boxlunch.com",
  verabradley: "https://verabradley.com",
  footlocker: "https://www.footlocker.com",
  champs: "https://www.champssports.com",
};

export const getRetailerHost = (retailer?: string | null): string =>
  RETAILER_HOSTS[normalizeRetailer(retailer)] || RETAILER_HOSTS.walmart;

const normalizeRetailer = (retailer?: string | null) =>
  (retailer || "walmart").toLowerCase().trim();

// Short, branded labels for the "view on retailer" button. Legacy retailers
// keep their established short forms; every other retailer derives a clean
// domain label from RETAILER_HOSTS so new retailers never mislabel as Walmart.
const RETAILER_LINK_LABELS: Record<string, string> = {
  walmart: "Walmart.com",
  walmart_online: "Walmart.com",
  target: "Target.com",
  target_online: "Target.com",
  amazon: "Amazon.com",
  home_depot: "HD.com",
  dollar_general: "DollarGeneral.com",
  cvs: "CVS.com",
  bestbuy: "BestBuy.com",
  lowes: "Lowes.com",
  tractor_supply: "TractorSupply.com",
  northern_tool: "NorthernTool.com",
  samsclub: "SamsClub.com",
  ace_hardware: "AceHardware.com",
  kohls: "Kohls.com",
  costco: "Costco.com",
  woot: "Woot.com",
};

export const getRetailerLinkLabel = (retailer?: string | null): string => {
  const key = normalizeRetailer(retailer);
  if (RETAILER_LINK_LABELS[key]) return RETAILER_LINK_LABELS[key];
  const host = RETAILER_HOSTS[key];
  if (host) return host.replace(/^https?:\/\//, "").replace(/^www\./, "");
  return "View product";
};

const appendPath = (host: string, path: string) =>
  `${host}${path.startsWith("/") ? "" : "/"}${path}`;

const normalizeDollarGeneralWebsiteUpc = (value: string) => {
  const digits = value.replace(/\D/g, "");
  return digits.length > 0 && digits.length < 12 ? digits.padStart(12, "0") : digits;
};

const repairDollarGeneralPlaceholderUrl = (value: string, host: string) => {
  const match = value.match(
    /^(?:https?:\/\/(?:www\.)?dollargeneral\.com)?\/p\/(\d+)\/?(?:[?#].*)?$/i
  );
  if (!match) return "";
  return `${host}/p/product/${normalizeDollarGeneralWebsiteUpc(match[1])}`;
};

export const normalizeExternalUrl = (value?: string | null): string => {
  const trimmed = value?.trim() || "";
  if (!trimmed) return "";

  const withProtocol = trimmed.startsWith("//") ? `https:${trimmed}` : trimmed;
  const nestedProtocolIndex = withProtocol.slice(1).search(/https?:\/\//i);
  if (nestedProtocolIndex >= 0) {
    return normalizeExternalUrl(withProtocol.slice(nestedProtocolIndex + 1));
  }

  if (/^https?:\/\//i.test(withProtocol)) return withProtocol;
  if (/^www\./i.test(withProtocol)) return `https://${withProtocol}`;
  return "";
};

export const appendStoreParam = (url: string, storeId?: string | null) => {
  if (!storeId || /[?&]store=/.test(url)) return url;
  return `${url}${url.includes("?") ? "&" : "?"}store=${encodeURIComponent(storeId)}`;
};

export const getRetailerProductUrl = ({
  url,
  retailer,
  storeId,
}: RetailerUrlInput): string => {
  const raw = url?.trim() || "";
  if (!raw) return "";

  const key = normalizeRetailer(retailer);
  const absoluteUrl = normalizeExternalUrl(raw);
  const host = RETAILER_HOSTS[key] || RETAILER_HOSTS.walmart;

  // Older DG crawler records used /p/<UPC>, but DG product routes require a
  // slug segment and the full UPC (the API drops leading zeroes).
  if (key === "dollar_general") {
    const repairedUrl = repairDollarGeneralPlaceholderUrl(raw, host);
    if (repairedUrl) return repairedUrl;
  }

  let productUrl = absoluteUrl;
  if (!productUrl) {
    if (raw.startsWith("/")) {
      productUrl = appendPath(host, raw);
    } else if (key === "walmart" || key === "walmart_online" || key === "samsclub") {
      productUrl = `${host}/ip/${encodeURIComponent(raw)}`;
    } else if (key === "target" || key === "target_online") {
      productUrl = `${host}/p/-/A-${encodeURIComponent(raw)}`;
    } else {
      productUrl = appendPath(host, raw);
    }
  }

  return key === "home_depot" ? appendStoreParam(productUrl, storeId) : productUrl;
};

export const buildRetailerLookupUrl = (
  retailer: string | null | undefined,
  value?: string | null,
) => {
  const raw = value?.trim() || "";
  if (!raw) return "";

  const absoluteUrl = normalizeExternalUrl(raw);
  if (absoluteUrl) return absoluteUrl;

  const key = normalizeRetailer(retailer);
  const id = encodeURIComponent(raw);
  switch (key) {
    case "home_depot":
      return `https://www.homedepot.com/p/${id}`;
    case "target":
    case "target_online":
      return `https://www.target.com/p/-/A-${id}`;
    case "dollar_general":
      return `https://www.dollargeneral.com/?q=${encodeURIComponent(normalizeDollarGeneralWebsiteUpc(raw))}`;
    case "bestbuy":
      return `https://www.bestbuy.com/site/searchpage.jsp?st=${id}`;
    case "costco":
      return `https://www.costco.com/CatalogSearch?dept=All&keyword=${id}`;
    case "amazon":
      return `https://www.amazon.com/s?k=${id}`;
    case "lowes":
      return `https://www.lowes.com/search?searchTerm=${id}`;
    case "tractor_supply":
      return `https://www.tractorsupply.com/tsc/search/${id}`;
    case "northern_tool":
      return `https://www.northerntool.com/search?query=${id}`;
    case "samsclub":
      return `https://www.samsclub.com/ip/${id}`;
    case "ace_hardware":
      return `https://www.acehardware.com/search?query=${id}`;
    case "kohls":
      return `https://www.kohls.com/search?q=${id}`;
    case "woot":
      return `https://www.woot.com`;
    case "newegg":
      return `https://www.newegg.com/p/pl?d=${id}`;
    case "gamestop":
      return `https://www.gamestop.com/search/?q=${id}`;
    case "bjs":
      return `https://www.bjs.com/search/${id}`;
    case "meijer":
      return `https://www.meijer.com/shopping/search.html?text=${id}`;
    case "menards":
      return `https://www.menards.com/main/search.html?search=${id}`;
    case "academy":
      return `https://www.academy.com/search?q=${id}`;
    case "walgreens":
      return `https://www.walgreens.com/search/results.jsp?Ntt=${id}`;
    case "zappos":
      return `https://www.zappos.com/search?term=${id}`;
    case "jcpenney":
      return `https://www.jcpenney.com/s/${id}`;
    case "belk":
      return `https://www.belk.com/search/?q=${id}`;
    case "michaels":
      return `https://www.michaels.com/search?q=${id}`;
    case "nike":
      return `https://www.nike.com/w?q=${id}`;
    case "adidas":
      return `https://www.adidas.com/us/search?q=${id}`;
    case "reebok":
      return `https://www.reebok.com/search?q=${id}`;
    case "skechers":
      return `https://www.skechers.com/search?q=${id}`;
    case "puma":
      return `https://us.puma.com/us/en/search?q=${id}`;
    case "underarmour":
      return `https://www.underarmour.com/c/search/?q=${id}`;
    case "columbia":
      return `https://www.columbia.com/search?q=${id}`;
    case "nordstromrack":
      return `https://www.nordstromrack.com/s?q=${id}`;
    case "aldo":
      return `https://www.aldoshoes.com/search?q=${id}`;
    case "vitacost":
      return `https://www.vitacost.com/search?w=${id}`;
    case "journeys":
      return `https://www.journeys.com/search?q=${id}`;
    case "shopdisney":
      return `https://www.disneystore.com/search?q=${id}`;
    case "ninjakitchen":
      return `https://www.ninjakitchen.com/search?q=${id}`;
    case "hottopic":
      return `https://www.hottopic.com/search?q=${id}`;
    case "officedepot":
      return `https://www.officedepot.com/catalog/search.do?Ntt=${id}`;
    case "barnesnoble":
      return `https://www.barnesandnoble.com/s/${id}`;
    case "razer":
      return `https://www.razer.com/search?q=${id}`;
    case "pacsun":
      return `https://www.pacsun.com/search?q=${id}`;
    case "bathbodyworks":
      return `https://www.bathandbodyworks.com/search?q=${id}`;
    case "staples":
      return `https://www.staples.com/search?query=${id}`;
    case "petsmart":
      return `https://www.petsmart.com/search/?q=${id}`;
    case "ulta":
      return `https://www.ulta.com/search?search=${id}`;
    case "forever21":
      return `https://www.forever21.com/search?q=${id}`;
    case "gap":
      return `https://www.gap.com/browse/search.do?searchText=${id}`;
    case "petco":
      return `https://www.petco.com/shop/en/petcostore/search?query=${id}`;
    case "americaneagle":
      return `https://www.ae.com/us/en/s/${id}`;
    case "aerie":
      return `https://www.ae.com/us/en/s/${id}`;
    case "carters":
      return `https://www.carters.com/search?q=${id}`;
    case "abercrombie":
      return `https://www.abercrombie.com/shop/us/search?searchTerm=${id}`;
    case "hollister":
      return `https://www.hollisterco.com/shop/us/search?searchTerm=${id}`;
    case "oldnavy":
      return `https://oldnavy.gap.com/browse/search.do?searchText=${id}`;
    case "bananarepublic":
      return `https://bananarepublic.gap.com/browse/search.do?searchText=${id}`;
    case "stevemadden":
      return `https://www.stevemadden.com/search?q=${id}`;
    case "athleta":
      return `https://athleta.gap.com/browse/search.do?searchText=${id}`;
    case "gapfactory":
      return `https://www.gapfactory.com/browse/search.do?searchText=${id}`;
    case "bananarepublicfactory":
      return `https://bananarepublicfactory.gapfactory.com/browse/search.do?searchText=${id}`;
    case "boxlunch":
      return `https://www.boxlunch.com/search?q=${id}`;
    case "verabradley":
      return `https://verabradley.com/search?q=${id}`;
    case "footlocker":
      return `https://www.footlocker.com/search?query=${id}`;
    case "champs":
      return `https://www.champssports.com/search?query=${id}`;
    default:
      return `https://www.walmart.com/ip/${id}`;
  }
};
